import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { getSalesCheck } from "@/lib/api";
import { Empty, TH, TR, errMsg } from "@/components/accounts/shared";

// Sales check (admins): people booked on each gym's GoHighLevel consult calendar for yesterday and today who
// showed up. Did they fill in the pre-sale questionnaire (QR)? If so, did they agree to recording? If not,
// was a consult still saved, and sold or not? Read-only (routes/sales_check.py).

const SOLD = new Set(["sold", "sold_pending_payment"]);
const STATUS = { sold: "Sold", sold_pending_payment: "Sold · pending payment", not_sold: "Not sold" };
const REC = { recorded: ["Recorded", "text-emerald-300"], declined: ["Declined", "text-sky-300"], missing: ["No recording", "text-rose-300"] };

const day = (iso, days) => (iso === days[1] ? "Today" : iso === days[0] ? "Yesterday" : iso || "—");
const time = (ts) => {
  const m = String(ts || "").match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
};
const YesNo = ({ v, yes = "Yes", no = "No", none = "—" }) =>
  v === true ? <span className="text-emerald-300">{yes}</span> : v === false ? <span className="text-rose-300 font-medium">{no}</span> : <span className="text-zinc-500">{none}</span>;

function Num({ n, tone }) {
  const cls = n === 0 ? "text-zinc-600" : { bad: "text-rose-300", good: "text-emerald-300", warn: "text-amber-300" }[tone] || "text-zinc-200";
  return <span className={`tabular-nums ${cls}`}>{n}</span>;
}

function Card({ title, children }) {
  return (
    <div className="card-surface px-4 py-3 space-y-2">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{title}</div>
      {children}
    </div>
  );
}

function Line({ label, n, tone }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-zinc-400">{label}</span>
      <span className="text-lg font-semibold"><Num n={n} tone={tone} /></span>
    </div>
  );
}

function People({ g }) {
  if (g.ghl_error) return <div className="px-4 py-3 text-xs text-rose-300">Couldn't read GoHighLevel: {g.ghl_error}</div>;
  if (!g.people.length) return <Empty>Nobody showed up for a booked consult.</Empty>;
  return (
    <table className="w-full text-sm min-w-[860px]">
      <thead><tr className={TH}>
        <th className="px-3 py-2 font-medium">Booked for</th><th className="px-3 py-2 font-medium">Name</th>
        <th className="px-3 py-2 font-medium">Questionnaire</th><th className="px-3 py-2 font-medium">Agreed to recording</th>
        <th className="px-3 py-2 font-medium">Consult in FPA</th><th className="px-3 py-2 font-medium">Recording</th>
        <th className="px-3 py-2 font-medium">How we know they showed</th>
      </tr></thead>
      <tbody>
        {g.people.map((p, i) => {
          const c = p.consult;
          const [recLabel, recCls] = c ? REC[c.recording] : ["—", "text-zinc-600"];
          return (
            <tr key={i} className={TR}>
              <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{day(p.appt_day, g.days)} {time(p.appt_at)}</td>
              <td className="px-3 py-2 text-zinc-200">{p.name}</td>
              <td className="px-3 py-2"><YesNo v={p.questionnaire} /></td>
              <td className="px-3 py-2">{p.questionnaire ? <YesNo v={p.recording_consent} none="Not asked" /> : <span className="text-zinc-600">—</span>}</td>
              <td className="px-3 py-2">{c
                ? <span className={SOLD.has(c.status) ? "text-emerald-300" : "text-zinc-300"}>{STATUS[c.status] || c.status}{c.rep ? <span className="text-zinc-500"> · {c.rep}</span> : null}</span>
                : <span className="text-rose-300">Not entered</span>}</td>
              <td className={`px-3 py-2 ${recCls}`}>{recLabel}</td>
              <td className="px-3 py-2 text-xs text-zinc-500">{p.showed_via.join(", ")}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const WATER = { yes: ["Yes", "text-emerald-300"], no: ["No", "text-rose-300 font-medium"], not_marked: ["Not marked", "text-amber-300"], not_asked: ["Not asked here", "text-zinc-500"] };

function LeadByLead({ gyms }) {
  const rows = gyms.flatMap((g) => g.leads.map((p, i) => ({ ...p, gym: g.gym, days: g.days, key: `${g.facility_id}:${i}` })));
  return (
    <div className="card-surface overflow-x-auto" data-testid="sales-check-leads">
      <div className="px-4 pt-3 pb-1">
        <div className="text-sm font-semibold text-zinc-100">Lead by lead <span className="text-zinc-500 font-normal">({rows.length})</span></div>
        <div className="text-[11px] text-zinc-500">Everyone who showed for a booked consult yesterday or today, plus consults held those days for people who weren't on the calendar.</div>
      </div>
      {rows.length === 0 ? <Empty>Nobody yet.</Empty> : (
        <table className="w-full text-sm min-w-[1080px]">
          <thead><tr className={TH}>
            <th className="px-3 py-2 font-medium">Gym</th><th className="px-3 py-2 font-medium">When</th>
            <th className="px-3 py-2 font-medium">Lead</th><th className="px-3 py-2 font-medium">Rep</th>
            <th className="px-3 py-2 font-medium">Questionnaire</th><th className="px-3 py-2 font-medium">Recording OK?</th>
            <th className="px-3 py-2 font-medium">Recorded</th><th className="px-3 py-2 font-medium">Water</th>
            <th className="px-3 py-2 font-medium">Outcome</th>
          </tr></thead>
          <tbody>
            {rows.map((p) => {
              const c = p.consult;
              const [recLabel, recCls] = c ? REC[c.recording] : ["—", "text-zinc-600"];
              const [wLabel, wCls] = WATER[p.water] || ["—", "text-zinc-600"];
              const consent = c?.recording === "declined" && p.recording_consent == null ? false : p.recording_consent;
              return (
                <tr key={p.key} className={TR}>
                  <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{p.gym}</td>
                  <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{day(p.appt_day, p.days)} {time(p.appt_at)}{!p.ghl_status && <span className="text-[10px] text-zinc-600"> · not on calendar</span>}</td>
                  <td className="px-3 py-2 text-zinc-100">{p.name}</td>
                  <td className="px-3 py-2 text-zinc-300">{c?.rep || (p.claimed_by ? <span className="text-amber-300">{p.claimed_by} · not saved</span> : <span className="text-rose-300">Unknown</span>)}</td>
                  <td className="px-3 py-2"><YesNo v={p.questionnaire} /></td>
                  <td className="px-3 py-2">{consent === true ? <span className="text-emerald-300">Agreed</span> : consent === false ? <span className="text-sky-300">Declined</span> : <span className="text-zinc-600">{p.questionnaire ? "Not asked" : "—"}</span>}</td>
                  <td className={`px-3 py-2 ${recCls}`}>{recLabel}</td>
                  <td className={`px-3 py-2 ${wCls}`}>{wLabel}</td>
                  <td className="px-3 py-2">{c ? <span className={SOLD.has(c.status) ? "text-emerald-300" : "text-zinc-300"}>{STATUS[c.status] || c.status}</span> : <span className="text-rose-300">Not in FPA</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function SalesCheck() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});

  const load = useCallback((refresh) => {
    setLoading(true);
    getSalesCheck(refresh)
      .then(setData)
      .catch((e) => toast.error(errMsg(e, "Could not load the sales check")))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(false); }, [load]);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const sum = (k) => data.gyms.reduce((s, g) => s + (g.totals[k] || 0), 0);
  const days = data.gyms[0]?.days || [];
  const water = (v) => data.gyms.reduce((n, g) => n + g.leads.filter((p) => p.water === v).length, 0);

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="sales-check-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow">Across every gym</div>
          <h1 className="text-2xl font-semibold text-zinc-50">Sales check</h1>
          <div className="text-xs text-zinc-500 mt-0.5">
            People booked on the GoHighLevel consult calendar for yesterday and today ({days.join(" and ")}) who showed up.
          </div>
        </div>
        <button onClick={() => load(true)} disabled={loading} data-testid="sales-check-refresh"
          className="inline-flex items-center gap-1.5 rounded-md border border-[#262626] px-3 h-9 text-sm text-zinc-200 hover:border-zinc-500 disabled:opacity-60">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-2">
        <Card title={`Showed up · ${sum("showed")} of ${sum("booked")} booked`}>
          <Line label="Filled in the questionnaire" n={sum("questionnaire")} tone="good" />
          <Line label="Did not fill it in" n={sum("no_questionnaire")} tone="bad" />
        </Card>
        <Card title={`Filled in the questionnaire · ${sum("questionnaire")}`}>
          <Line label="Agreed to recording" n={sum("consent_yes")} tone="good" />
          <Line label="Said no to recording" n={sum("consent_no")} tone="warn" />
          <Line label="Not asked (older form)" n={sum("consent_not_asked")} />
        </Card>
        <Card title={`Did not fill it in · ${sum("no_questionnaire")}`}>
          <Line label="Consult saved · sold" n={sum("no_q_sold")} tone="bad" />
          <Line label="Consult saved · not sold" n={sum("no_q_not_sold")} tone="bad" />
          <Line label="Showed, but no consult in FPA" n={sum("no_q_no_consult")} tone="bad" />
        </Card>
        <Card title="Bottle of water · questionnaires in India">
          <Line label="Given" n={water("yes")} tone="good" />
          <Line label="Not given" n={water("no")} tone="bad" />
          <Line label="Not marked" n={water("not_marked")} tone="warn" />
        </Card>
      </div>

      <LeadByLead gyms={data.gyms} />

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead><tr className={TH}>
            <th className="px-3 py-2 font-medium">Gym</th>
            <th className="px-3 py-2 font-medium text-right">Booked</th>
            <th className="px-3 py-2 font-medium text-right">Showed</th>
            <th className="px-3 py-2 font-medium text-right">Questionnaire yes</th>
            <th className="px-3 py-2 font-medium text-right">Questionnaire no</th>
            <th className="px-3 py-2 font-medium text-right">Agreed to recording</th>
            <th className="px-3 py-2 font-medium text-right">Said no to recording</th>
            <th className="px-3 py-2 font-medium text-right">No QR · sold</th>
            <th className="px-3 py-2 font-medium text-right">No QR · not sold</th>
            <th className="px-3 py-2 font-medium text-right">No QR · not in FPA</th>
          </tr></thead>
          <tbody>
            {data.gyms.map((g) => {
              const t = g.totals;
              const isOpen = !!open[g.facility_id];
              return (
                <Fragment key={g.facility_id}>
                  <tr className={`${TR} cursor-pointer`} onClick={() => setOpen((o) => ({ ...o, [g.facility_id]: !isOpen }))}
                    data-testid={`sales-check-row-${g.facility_id}`}>
                    <td className="px-3 py-2.5 text-zinc-100 font-medium whitespace-nowrap">
                      {isOpen ? <ChevronDown className="inline h-3.5 w-3.5 mr-1 text-zinc-500" /> : <ChevronRight className="inline h-3.5 w-3.5 mr-1 text-zinc-500" />}
                      {g.gym}
                      {g.ghl_error && <span className="ml-2 text-[10px] text-rose-300">GHL unreadable</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right"><Num n={t.booked} /></td>
                    <td className="px-3 py-2.5 text-right"><Num n={t.showed} /></td>
                    <td className="px-3 py-2.5 text-right"><Num n={t.questionnaire} tone="good" /></td>
                    <td className="px-3 py-2.5 text-right"><Num n={t.no_questionnaire} tone="bad" /></td>
                    <td className="px-3 py-2.5 text-right"><Num n={t.consent_yes} tone="good" /></td>
                    <td className="px-3 py-2.5 text-right"><Num n={t.consent_no} tone="warn" /></td>
                    <td className="px-3 py-2.5 text-right"><Num n={t.no_q_sold} tone="bad" /></td>
                    <td className="px-3 py-2.5 text-right"><Num n={t.no_q_not_sold} tone="bad" /></td>
                    <td className="px-3 py-2.5 text-right"><Num n={t.no_q_no_consult} tone="bad" /></td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-[#1a1a1a] bg-[#0c0c0c]">
                      <td colSpan={10} className="p-0" data-testid={`sales-check-detail-${g.facility_id}`}>
                        <div className="px-3 pt-2 text-[11px] text-zinc-500">
                          {g.calendars.join(", ") || "No consult calendar"} · {t.booked} booked · {t.showed} showed · {t.noshow} no-show · {t.cancelled} cancelled · {t.not_marked} past and not marked · {t.upcoming} still to come today
                        </div>
                        <People g={g} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-zinc-500 space-y-0.5">
        <div>Someone counts as <b className="text-zinc-400">showed</b> when GoHighLevel marked their appointment showed, they filled in the questionnaire, or a consult for them was saved in FPA. People are matched by phone, then email.</div>
        <div>Consults typed into FPA for people who weren't on the calendar these two days aren't counted here. Updated {new Date(data.generated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.</div>
      </div>
    </div>
  );
}
